import React, { ReactNode, useState } from 'react'
import headlessToast, { Toast } from 'react-hot-toast/headless'
import { Box, Icon, IconButton, IconProps, Text } from '@ui'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { TextProps } from 'ui/components/Text/Text'

export type Props<T extends IconProps['type'] | undefined = IconProps['type']> = {
    toast: Toast
    icon?: T
    iconProps?: Partial<IconProps>
    imgSrc?: string
    pending?: T extends IconProps['type'] ? never : boolean
    success?: boolean
    message: string | ReactNode
    subMessage?: string
    cta?: string
    ctaColor?: TextProps['color']
    onCtaClick?: ({ dismissToast }: { dismissToast: () => void }) => void | Promise<void>
    onDismiss?: () => void
    iconAnimation?: React.JSX.Element
    preventClamp?: boolean
}

export function StandardToast<T extends IconProps['type'] | undefined>(props: Props<T>) {
    const {
        icon,
        iconProps,
        imgSrc,
        message,
        subMessage,
        onDismiss,
        toast,
        cta,
        ctaColor,
        onCtaClick,
        pending,
        iconAnimation,
        success,
        preventClamp = false,
    } = props
    const [ctaActionLoading, setCtaActionLoading] = useState(false)
    const _onCtaClick = async () => {
        if (ctaActionLoading) {
            return
        }
        setCtaActionLoading(true)
        await onCtaClick?.({
            dismissToast: () => headlessToast.dismiss(toast.id),
        })
        setCtaActionLoading(false)
    }
    const [seeMore, setSeeMore] = useState(false)

    const isUnknownConnectorError = subMessage?.toLowerCase().includes('unknown connector')

    return (
        <Box
            horizontal
            gap
            width={{
                mobile: '300',
                desktop: '390',
            }}
            alignItems="center"
            justifyContent="spaceBetween"
        >
            <Box alignSelf="start">
                {success && iconAnimation ? <Box position="absolute"> {iconAnimation} </Box> : null}
                <Box
                    horizontal
                    gap
                    centerContent
                    width="x4"
                    background="level3"
                    rounded="sm"
                    position="relative"
                    aspectRatio="1/1"
                    overflow="hidden"
                >
                    {imgSrc && (
                        <Box
                            absoluteFill
                            style={
                                imgSrc
                                    ? {
                                          backgroundImage: `url(${imgSrc})`,
                                          backgroundSize: 'cover',
                                          backgroundPosition: 'center',
                                          opacity: pending || icon ? 0.25 : 1,
                                      }
                                    : undefined
                            }
                            alt="notification"
                        />
                    )}
                    {icon && <Icon shrink={false} {...iconProps} type={icon} size="square_sm" />}
                    {pending && <ButtonSpinner />}
                </Box>
            </Box>
            <Box gap grow overflowX="scroll" overflowY="hidden">
                <Box gap="sm">
                    <Box>{message}</Box>
                    {isUnknownConnectorError && (
                        <Box>(UC) Please try logging out and in again.</Box>
                    )}

                    {subMessage && !isUnknownConnectorError && !seeMore && (
                        <Box
                            style={{
                                display: '-webkit-box',
                                WebkitLineClamp: preventClamp ? undefined : 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                            color="gray2"
                            fontSize="sm"
                        >
                            {subMessage}
                        </Box>
                    )}

                    {subMessage && !isUnknownConnectorError && seeMore && (
                        <Box color="gray2" fontSize="sm">
                            {subMessage}
                        </Box>
                    )}

                    {subMessage && subMessage.length > 100 && !seeMore && (
                        <Box
                            paddingBottom="sm"
                            cursor="pointer"
                            alignSelf="end"
                            onClick={() => setSeeMore(!seeMore)}
                        >
                            <Text size="sm">See more</Text>
                        </Box>
                    )}
                </Box>
                {cta && (
                    <Box horizontal gap alignItems="center">
                        {ctaActionLoading && <ButtonSpinner />}
                        <Box
                            cursor="pointer"
                            opacity={ctaActionLoading ? '0.5' : 'opaque'}
                            disabled={ctaActionLoading}
                            paddingBottom="xs"
                            onClick={_onCtaClick}
                        >
                            <Text color={ctaColor}>{cta}</Text>
                        </Box>
                    </Box>
                )}
            </Box>
            <Box alignSelf="start">
                {!pending && (
                    <IconButton
                        alignSelf="center"
                        shrink={false}
                        size="square_sm"
                        icon="close"
                        color="default"
                        paddingTop="none"
                        onClick={() => {
                            onDismiss?.()
                            headlessToast.dismiss(toast.id)
                        }}
                    />
                )}
            </Box>
        </Box>
    )
}

StandardToast.Success = (props: Props) => (
    <StandardToast
        icon={props.imgSrc ? undefined : 'check'}
        iconProps={props.imgSrc ? undefined : { color: 'positive' }}
        ctaColor="positive"
        {...props}
    />
)

StandardToast.Error = (props: Props) => (
    <StandardToast icon="alert" iconProps={{ color: 'error' }} ctaColor="negative" {...props} />
)

StandardToast.Pending = (props: Props<undefined>) => <StandardToast pending {...props} />

export function dismissToast(toastId: string) {
    headlessToast.dismiss(toastId)
}
